import styled from "@emotion/styled";
import { FC, RefObject, useState } from "react";

import { Add, Close } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  FormField,
  Icon,
  Input,
  RadiusToken,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import { withVar } from "themes/helpers";

interface IProps {
  placeholder: string;
  selectedOptions: string[];
  onOptionAdd: (value: string) => void;
  onOptionRemove: (value: string) => void;
  inputRef?: RefObject<HTMLInputElement>;
  validateInput?: (value: string) => string | null;
  validateOption?: (value: string) => string | null;
}

export const MultiOptionInput: FC<IProps> = ({
  placeholder,
  selectedOptions,
  onOptionAdd,
  onOptionRemove,
  inputRef,
  validateInput,
  validateOption,
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [valueSubmitted, setValueSubmitted] = useState(false);

  const errorMessage = validateInput?.(inputValue) ?? undefined;

  const showErrorMessage = valueSubmitted && errorMessage;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value);
    if (valueSubmitted) {
      setValueSubmitted(false);
    }
  };

  const handleAddOption = (): void => {
    setValueSubmitted(true);

    if (errorMessage || inputValue.trim().length === 0) {
      return;
    }

    onOptionAdd(inputValue);
    setInputValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddOption();
    }
  };

  return (
    <>
      <FormContainer onSubmit={e => e.preventDefault()}>
        <InputContainer error={showErrorMessage ? errorMessage : undefined}>
          <Input
            ref={inputRef}
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            error={!!showErrorMessage}
          />
        </InputContainer>
        <Button
          kind="high-contrast"
          icon={<Icon source={Add} />}
          onClick={handleAddOption}
          disabled={!!showErrorMessage}
        />
      </FormContainer>
      {selectedOptions.length > 0 && (
        <SelectedOptionsContainer>
          {selectedOptions.map(option => (
            <SelectedOption
              key={option}
              value={option}
              error={!!validateOption?.(option)}
              onRemove={() => onOptionRemove(option)}
            />
          ))}
        </SelectedOptionsContainer>
      )}
    </>
  );
};

interface ISelectedOptionProps {
  value: string;
  error: boolean;
  onRemove: () => void;
}

const SelectedOption: FC<ISelectedOptionProps> = ({ value, error, onRemove }) => {
  return (
    <SelectedOptionContainer error={error}>
      <Value>{value}</Value>
      <Button
        kind="plain"
        size="xcompact"
        icon={
          <Icon
            source={Close}
            customColor={withVar(error ? DesignToken.ContentBasicNegative : DesignToken.ContentBasicPrimary)}
          />
        }
        onClick={onRemove}
      />
    </SelectedOptionContainer>
  );
};

const SelectedOptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(${SpacingToken.Spacing1});
`;

const SelectedOptionContainer = styled.div<{ error: boolean }>`
  display: flex;
  align-items: center;
  width: fit-content;
  max-width: 100%;
  border-radius: ${withVar(RadiusToken.Radius3)};
  padding-left: ${withVar(SpacingToken.Spacing4)};
  padding-right: ${withVar(SpacingToken.Spacing2)};
  gap: ${withVar(SpacingToken.Spacing1)};
  min-height: ${withVar(SpacingToken.Spacing9)};
  background-color: ${({ error }): string =>
    withVar(error ? DesignToken.SurfaceAccentEmphasisLowNegative : DesignToken.SurfaceTertiaryDefault)};
  color: ${({ error }): string => withVar(error ? DesignToken.ContentBasicNegative : DesignToken.ContentBasicPrimary)};
  box-sizing: border-box;
`;

const Value = styled(Text)`
  word-break: break-all;
  margin: ${withVar(SpacingToken.Spacing2)} 0;
`;

const FormContainer = styled.form`
  display: flex;
  gap: ${withVar(SpacingToken.Spacing1)};
`;

const InputContainer = styled(FormField)`
  width: 100%;
  margin-bottom: 0;
`;
